import { ToastMessageInterface } from "@app/interfaces/ToastMessageInterface";
import { RoutesEnum, RoutesParametersEnum } from "@app/models/RoutesEnum";
import { CustomEndpointService } from "@app/services/CustomEndpointService";
import {
    CreateCustomEndpointDto,
    CustomEndpointInterface,
} from "@dashart/dashart-gpt-shared-library";
import UserInterface from "@dashart/dashart-gpt-shared-library/dist/interfaces/UserInterface";
import ButtonWithLink from "@view/components/buttonWithLink/ButtonWithLink";
import ConfirmDialog from "@view/components/confirmDialog/ConfirmDialog";
import FormValidationErrorFieldset from "@view/components/formValidationErrorFieldset/FormValidationErrorFieldset";
import BaseInput, { BaseInputType } from "@view/components/inputs/baseInput/BaseInput";
import ProgressSpinnerOverlay from "@view/components/progressSpinnerOverlay/ProgressSpinnerOverlay";
import { transformAndValidateSync } from "class-transformer-validator";
import { ValidationError } from "class-validator";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Fieldset } from "primereact/fieldset";
import { InputNumber } from "primereact/inputnumber";
import { Panel } from "primereact/panel";
import * as React from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import "./CustomEndpointDetailScreen.scss";

export enum CustomEndpointDetailScreenMode {
    CREATE = "CREATE",
    UPDATE = "UPDATE",
    DELETE = "DELETE",
    VIEW = "VIEW",
}

export interface CustomEndpointDetailScreenProps {
    currentUser: UserInterface;
    mode: CustomEndpointDetailScreenMode;
    showToast: (toast: ToastMessageInterface) => void;
}

interface FormData {
    endpointSlug?: string;
    name?: string | null;
    baseUrl?: string;
    apiKey?: string | null;
    defaultModel?: string | null;
    models?: string[];
    active?: boolean;
    extraHeaders?: Record<string, string> | null;
    maxTokens?: number | null;
}

/**
 * Builds a blank FormData with sensible defaults (active=true, empty fields).
 * Used as the initial state for the CREATE mode.
 *
 * @returns a fresh empty FormData
 */
const emptyForm = (): FormData => ({
    endpointSlug: '',
    name: '',
    baseUrl: '',
    apiKey: '',
    defaultModel: '',
    models: [],
    active: true,
    extraHeaders: null,
    maxTokens: null,
});

/**
 * Detail screen for a single custom endpoint, used in CREATE / UPDATE / VIEW /
 * DELETE modes. Loads the entity by `:endpointId` URL param when not creating,
 * validates with CreateCustomEndpointDto, and delegates persistence to
 * CustomEndpointService.
 *
 * @param props.currentUser the authenticated user
 * @param props.mode current screen mode
 * @param props.showToast callback to surface success/error notifications
 * @returns the rendered detail panel
 */
const CustomEndpointDetailScreen: React.FunctionComponent<CustomEndpointDetailScreenProps> = (props) => {
    const { mode } = props;
    const { t } = useTranslation();
    const { endpointId } = useParams();
    const navigate = useNavigate();

    const [serverActionPending, setServerActionPending] = useState(false);
    const [formData, setFormData] = useState<FormData>(emptyForm());
    const [formErrors, setFormErrors] = useState<ValidationError[]>([]);
    const [modelsInput, setModelsInput] = useState<string>('');
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

    const isEditMode = mode === CustomEndpointDetailScreenMode.CREATE || mode === CustomEndpointDetailScreenMode.UPDATE;
    const isCreateMode = mode === CustomEndpointDetailScreenMode.CREATE;

    /**
     * Loads an endpoint by numeric id and hydrates the form. Shows an error
     * toast and navigates back to the list on failure.
     *
     * @param id numeric endpoint id
     */
    const loadEndpoint = async (id: number) => {
        setServerActionPending(true);
        try {
            const entity = await CustomEndpointService.getById(id);
            applyEntityToForm(entity);
        } catch (error) {
            props.showToast({
                severity: "error",
                summary: t('globals.loadFailedNotice'),
                detail: JSON.stringify(error ?? ''),
            });
            navigate(RoutesEnum.customEndpointList);
        }
        setServerActionPending(false);
    };

    /**
     * Copies persisted entity fields into the form state and seeds the
     * comma-separated models input.
     *
     * @param entity loaded CustomEndpoint
     */
    const applyEntityToForm = (entity: CustomEndpointInterface) => {
        setFormData({
            endpointSlug: entity.endpointSlug,
            name: entity.name,
            baseUrl: entity.baseUrl,
            apiKey: entity.apiKey,
            defaultModel: entity.defaultModel,
            models: entity.models ?? [],
            active: entity.active,
            extraHeaders: entity.extraHeaders,
            maxTokens: entity.maxTokens,
        });
        setModelsInput((entity.models ?? []).join(', '));
    };

    useEffect(() => {
        if (endpointId && !isCreateMode) {
            loadEndpoint(parseInt(endpointId, 10));
        }
    }, [endpointId]);

    /**
     * Splits the comma-separated models input into a trimmed, non-empty list.
     *
     * @param raw user-entered comma list
     * @returns clean model id array
     */
    const parseModels = (raw: string): string[] => {
        return raw.split(',').map(s => s.trim()).filter(Boolean);
    };

    /**
     * Runs class-validator against CreateCustomEndpointDto and stores any
     * resulting errors on local state.
     *
     * @param data current form data to validate
     * @returns the array of ValidationError (empty when valid)
     */
    const validateInputs = (data: FormData): ValidationError[] => {
        try {
            transformAndValidateSync(CreateCustomEndpointDto, {
                endpointSlug: data.endpointSlug,
                name: data.name ?? undefined,
                baseUrl: data.baseUrl,
                apiKey: data.apiKey ?? undefined,
                defaultModel: data.defaultModel ?? undefined,
                models: data.models ?? [],
                active: data.active,
                extraHeaders: data.extraHeaders ?? undefined,
                maxTokens: data.maxTokens ?? undefined,
            } as any);
            setFormErrors([]);
            return [];
        } catch (err) {
            setFormErrors(err as ValidationError[]);
            return err as ValidationError[];
        }
    };

    /**
     * Merges a partial patch into the form data and re-runs validation.
     *
     * @param patch fields to update
     */
    const updateField = (patch: Partial<FormData>) => {
        const next = { ...formData, ...patch };
        validateInputs(next);
        setFormData(next);
    };

    /**
     * Validates the form, then either creates a new endpoint (CREATE mode)
     * or patches the existing one (UPDATE mode). Surfaces toasts on the
     * outcome and navigates to the edit screen of the new entity on create.
     */
    const onSaveClickHandler = async () => {
        const dataToValidate: FormData = {
            ...formData,
            models: parseModels(modelsInput),
        };
        const errors = validateInputs(dataToValidate);
        if (errors?.length > 0) {
            props.showToast({
                severity: "error",
                summary: t('globals.validationFailedNotice'),
            });
            return;
        }

        setServerActionPending(true);
        try {
            const dto = {
                endpointSlug: dataToValidate.endpointSlug,
                name: dataToValidate.name ?? undefined,
                baseUrl: dataToValidate.baseUrl,
                apiKey: dataToValidate.apiKey ?? undefined,
                defaultModel: dataToValidate.defaultModel ?? undefined,
                models: dataToValidate.models,
                active: dataToValidate.active,
                extraHeaders: dataToValidate.extraHeaders ?? undefined,
                maxTokens: dataToValidate.maxTokens ?? undefined,
            };

            if (isCreateMode) {
                const created = await CustomEndpointService.create(dto as any);
                props.showToast({ severity: "success", summary: t('globals.createSuccessNotice') });
                navigate(RoutesEnum.customEndpointEdit.replace(RoutesParametersEnum.endpointId, `${created.id}`));
            } else {
                await CustomEndpointService.update(parseInt(endpointId!, 10), dto as any);
                props.showToast({ severity: "success", summary: t('globals.updateSuccessNotice') });
            }
        } catch (error) {
            props.showToast({
                severity: "error",
                summary: isCreateMode ? t('globals.createFailedNotice') : t('globals.updateFailedNotice'),
                detail: JSON.stringify(error ?? ''),
            });
        }
        setServerActionPending(false);
    };

    /**
     * Confirms a delete after the user accepts the ConfirmDialog. Soft-deletes
     * the endpoint, surfaces a toast, and returns to the list screen.
     */
    const onDeleteConfirm = async () => {
        setShowDeleteConfirmation(false);
        setServerActionPending(true);
        try {
            await CustomEndpointService.delete(parseInt(endpointId!, 10));
            props.showToast({ severity: "success", summary: t('globals.deleteSuccessNotice') });
            navigate(RoutesEnum.customEndpointList);
        } catch (error) {
            props.showToast({
                severity: "error",
                summary: t('globals.deleteFailedNotice'),
                detail: JSON.stringify(error ?? ''),
            });
        }
        setServerActionPending(false);
    };

    /**
     * Renders the bottom action button group for the current mode
     * (create/save/delete/back-to-overview).
     *
     * @returns the action button group
     */
    const actionButtons = () => {
        if (mode === CustomEndpointDetailScreenMode.CREATE) {
            return <>
                <ButtonWithLink className="button-secondary" label={t('globals.actions.cancel')} icon="pi pi-times" href={RoutesEnum.customEndpointList} />
                <Button className="ml-2" label={t('globals.actions.create')} icon="pi pi-check" onClick={onSaveClickHandler} />
            </>;
        }
        if (mode === CustomEndpointDetailScreenMode.UPDATE) {
            return <>
                <ButtonWithLink className="button-secondary" label={t('globals.actions.dismiss')} icon="pi pi-times" href={RoutesEnum.customEndpointList} />
                <Button className="ml-2" label={t('globals.actions.save')} icon="pi pi-check" onClick={onSaveClickHandler} />
            </>;
        }
        if (mode === CustomEndpointDetailScreenMode.DELETE) {
            return <>
                <ButtonWithLink className="button-secondary" label={t('globals.actions.backToOverview')} icon="pi pi-chevron-left" href={RoutesEnum.customEndpointList} />
                <Button className="ml-2 button-danger" label={t('globals.actions.delete')} icon="pi pi-trash" onClick={() => setShowDeleteConfirmation(true)} />
            </>;
        }
        return <ButtonWithLink className="button-secondary" label={t('globals.actions.backToOverview')} icon="pi pi-chevron-left" href={RoutesEnum.customEndpointList} />;
    };

    /**
     * Renders the delete ConfirmDialog when the user has clicked the delete
     * button; null otherwise.
     *
     * @returns the ConfirmDialog element or null
     */
    const deleteConfirmation = () => {
        if (!showDeleteConfirmation) return null;
        return <ConfirmDialog
            headline={t('custom_endpoints.deleteConfirmation.headline')}
            body={t('custom_endpoints.deleteConfirmation.body', formData as any) as unknown as string}
            confirmLabel={t('globals.actions.yes')}
            cancelLabel={t('globals.actions.no')}
            onConfirm={onDeleteConfirm}
            onCancel={() => setShowDeleteConfirmation(false)}
            onHide={() => setShowDeleteConfirmation(false)}
        />;
    };

    return <div className='CustomEndpointDetailScreen d-list-screen'>
        {deleteConfirmation()}
        <Panel header={t('custom_endpoints.detail_screen.header')} className={'p-4'}>
            <ProgressSpinnerOverlay visible={serverActionPending} />

            <div className={'detail-container'}>

                <Fieldset legend={t('custom_endpoints.detail_screen.basicsHeader')} className={'mb-4'}>
                    <div className="p-fluid formgrid grid">
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-6'}
                            label={t('custom_endpoints.fields.endpointSlug')}
                            value={formData?.endpointSlug}
                            valueName={'endpointSlug'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => updateField({ endpointSlug: v })}
                        />
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-6'}
                            label={t('custom_endpoints.fields.name')}
                            value={formData?.name}
                            valueName={'name'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => updateField({ name: v })}
                        />
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-12'}
                            label={t('custom_endpoints.fields.baseUrl')}
                            value={formData?.baseUrl}
                            valueName={'baseUrl'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => updateField({ baseUrl: v })}
                        />
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-12'}
                            label={t('custom_endpoints.fields.apiKey')}
                            value={formData?.apiKey}
                            valueName={'apiKey'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => updateField({ apiKey: v })}
                        />
                        <div className="col-12 mb-3">
                            <div className="p-field-checkbox">
                                <Checkbox
                                    inputId="active"
                                    checked={formData?.active ?? false}
                                    onChange={(e) => updateField({ active: !!e.checked })}
                                    disabled={!isEditMode}
                                />
                                <label htmlFor="active" className="ml-2">
                                    {t('custom_endpoints.fields.active')}
                                </label>
                            </div>
                        </div>
                    </div>
                </Fieldset>

                <Fieldset legend={t('custom_endpoints.detail_screen.modelsHeader')} className={'mb-4'}>
                    <div className="p-fluid formgrid grid">
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-12'}
                            label={t('custom_endpoints.fields.defaultModel')}
                            value={formData?.defaultModel}
                            valueName={'defaultModel'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => updateField({ defaultModel: v })}
                        />
                        <BaseInput
                            type={BaseInputType.TextInput}
                            classes={'col-12'}
                            label={t('custom_endpoints.fields.models')}
                            value={modelsInput}
                            valueName={'models'}
                            formErrors={formErrors}
                            readOnly={!isEditMode}
                            handlerChange={(v: any) => {
                                setModelsInput(v as string);
                                updateField({ models: parseModels(v as string) });
                            }}
                        />
                        <div className="col-12 mb-2 text-sm opacity-70">
                            {t('custom_endpoints.fields.modelsHint')}
                        </div>
                    </div>
                </Fieldset>

                <Fieldset legend={t('custom_endpoints.detail_screen.advancedHeader')} className={'mb-4'}>
                    <div className="p-fluid formgrid grid">
                        <div className="col-6 mb-3">
                            <label className="block mb-2">{t('custom_endpoints.fields.maxTokens')}</label>
                            <InputNumber
                                value={formData?.maxTokens ?? null}
                                onChange={(e) => updateField({ maxTokens: e.value ?? null })}
                                disabled={!isEditMode}
                                min={1}
                                showButtons={false}
                                useGrouping={false}
                            />
                        </div>
                    </div>
                </Fieldset>

                <div className={'messages-area'}>
                    {formErrors?.length > 0 && <FormValidationErrorFieldset
                        headline={t('custom_endpoints.detail_screen.label_validation_errors')}
                        translationKeyBase={'custom_endpoints.fields.'}
                        validationErrors={formErrors}
                    />}
                </div>

                <div className="col-12 d-flex justify-end">
                    {actionButtons()}
                </div>
            </div>
        </Panel>
    </div>;
};

export default CustomEndpointDetailScreen;
